/**
 * Clive: show the running total inside the Add to Cart button (Adam, 2026-09-11).
 *
 * The button reads "Add to Cart · $1,404" and the figure tracks everything that can
 * change it before submit:
 *   - the variant picker          (product:variant-change)
 *   - the quantity stepper        (input[name="quantity"] inside the form)
 *   - the Customize add-ons       (snippets/product-block-addons.liquid checkboxes)
 *   - the subscription picker     (snippets/product-block-subscription.liquid)
 *
 * Why a separate span: theme.js rewrites [data-add-to-cart-text] on every variant
 * change to swap between the available / sold out / unavailable labels, so anything
 * written into that span is clobbered on the next change. The total goes into its own
 * [data-clive-atc-price] sibling, which theme.js does not know about.
 *
 * Every figure is read in CENTS from a data attribute Liquid rendered, never parsed
 * back out of a formatted money string — parsing "$1,404.00" breaks the moment a
 * market uses a different separator. Only the final sum is formatted, through
 * window.theme.moneyFormat, which is the shop's own format string.
 *
 * Degrades to nothing: the span ships empty and hidden, so with JS off, or before this
 * runs, the button reads exactly as it did before.
 */
(function () {
  var PRICE_SELECTOR = '[data-clive-atc-price]';

  /**
   * Format cents using the shop's money_format. Handles the four tokens Shopify
   * supports; anything unrecognised falls back to a plain dollar figure rather than
   * rendering a broken string.
   */
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';

    function withCommas(num) {
      var parts = num.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    function value(token) {
      var amount = cents / 100;
      switch (token) {
        case 'amount':
          return withCommas(amount.toFixed(2));
        case 'amount_no_decimals':
          return withCommas(Math.round(amount).toString());
        case 'amount_with_comma_separator':
          return withCommas(amount.toFixed(2)).replace(/[.,]/g, function (c) {
            return c === ',' ? '.' : ',';
          });
        case 'amount_no_decimals_with_comma_separator':
          return withCommas(Math.round(amount).toString()).replace(/,/g, '.');
        default:
          return null;
      }
    }

    var matched = false;
    var out = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (whole, token) {
      var v = value(token);
      if (v === null) return whole;
      matched = true;
      return v;
    });

    if (!matched) out = '$' + withCommas((cents / 100).toFixed(2));

    // Match the PDP price, which Stiletto renders with money_without_trailing_zeros:
    // a whole-dollar total shows as $1,199, not $1,199.00. Only a trailing zero pair is
    // dropped, so $1,199.50 keeps its cents.
    if (cents % 100 === 0) out = out.replace(/([.,])00(?!\d)/, '');

    return out;
  }

  function section(el) {
    return el.closest('.shopify-section') || document;
  }

  function forms() {
    return document.querySelectorAll('[data-product-form]');
  }

  function quantity(form) {
    var input = form.querySelector('input[name="quantity"]');
    var n = input ? parseInt(input.value, 10) : 1;
    return n > 0 ? n : 1;
  }

  /**
   * Unit price in cents for the currently selected variant, subscription-aware.
   * When a selling plan is active the subscription block has already pre-rendered that
   * allocation's price, so the discount is taken from Shopify's own figure rather than
   * recomputed from a percentage here.
   */
  function unitCents(form, scope) {
    var select = form.querySelector('[data-variant-select]');
    var variantId = select ? select.value : null;
    if (!variantId) return null;

    var planInput = form.querySelector('[data-clive-selling-plan-input]');
    var planId = planInput && !planInput.disabled ? planInput.value : '';

    if (planId) {
      var alloc = scope.querySelector(
        '[data-clive-sub-variant="' + variantId + '"][data-clive-sub-plan="' + planId + '"]'
      );
      var allocCents = alloc && alloc.getAttribute('data-clive-sub-cents');
      if (allocCents) return parseInt(allocCents, 10);
    }

    var oneTime = scope.querySelector('[data-clive-sub-onetime-for="' + variantId + '"]');
    var oneTimeCents = oneTime && oneTime.getAttribute('data-clive-sub-onetime-cents');
    if (oneTimeCents) return parseInt(oneTimeCents, 10);

    // No subscription block on this product (most of the catalogue). Liquid stamped the
    // starting price on the form and theme.js keeps it current via variant-change.
    var stamped = form.getAttribute('data-clive-variant-cents');
    return stamped ? parseInt(stamped, 10) : null;
  }

  /**
   * Checked Customize add-ons. Reads data-addon-cents, which Liquid sets to 0 for the
   * free-by-default variant so the total matches the $0 the row displays.
   */
  function addonCents(scope) {
    var checked = scope.querySelectorAll('.clive-addon__checkbox:checked');
    var total = 0;
    for (var i = 0; i < checked.length; i++) {
      var cents = checked[i].getAttribute('data-addon-cents');
      if (cents !== null) {
        total += parseInt(cents, 10) || 0;
      } else {
        // Older markup carried dollars only.
        var dollars = parseFloat(checked[i].getAttribute('data-addon-price'));
        if (!isNaN(dollars)) total += Math.round(dollars * 100);
      }
    }
    return total;
  }

  function available(form) {
    var btn = form.querySelector('[data-add-to-cart]');
    return !!btn && !btn.disabled;
  }

  function update(form) {
    var scope = section(form);
    var nodes = scope.querySelectorAll(PRICE_SELECTOR);
    if (!nodes.length) return;

    var unit = unitCents(form, scope);

    // Sold out or unavailable: the label already says so and a price beside it would
    // read as a live offer. Hide rather than show a stale figure.
    if (unit === null || !available(form)) {
      for (var h = 0; h < nodes.length; h++) nodes[h].hidden = true;
      return;
    }

    var total = unit * quantity(form) + addonCents(scope);
    var text = formatMoney(total);
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = text;
      nodes[i].hidden = false;
    }

    updateStickyBar(scope, unit, total, text);
  }

  /**
   * The sticky bar carries its own price. Swap it for the running total only when the
   * two differ — an add-on is checked or the quantity is above one. Leaving the native
   * [data-price] in place the rest of the time keeps Stiletto's compare-at strikethrough
   * on sale items, which replacing it unconditionally would throw away.
   */
  function updateStickyBar(scope, unit, total, text) {
    var wrap = scope.querySelector('.sticky-atc-bar__price');
    if (!wrap) return;
    var node = wrap.querySelector('[data-clive-sticky-total]');
    if (!node) return;

    if (total === unit) {
      node.hidden = true;
      wrap.classList.remove('clive-sticky-has-total');
      return;
    }

    node.textContent = text;
    node.hidden = false;
    wrap.classList.add('clive-sticky-has-total');
  }

  function updateAll() {
    var all = forms();
    for (var i = 0; i < all.length; i++) update(all[i]);
  }

  function formFromEvent(target) {
    if (!target || !target.closest) return null;
    var scope = target.closest('.shopify-section') || document;
    return scope.querySelector('[data-product-form]');
  }

  // One delegated listener rather than per-control wiring: the add-on checkboxes, the
  // quantity input and the subscription controls all live outside the form, and the
  // quick view injects more of them after load.
  document.addEventListener('change', function (evt) {
    var t = evt.target;
    if (!t || !t.matches) return;
    if (
      t.matches('.clive-addon__checkbox') ||
      t.matches('input[name="quantity"]') ||
      t.matches('[data-quantity-input]') ||
      t.matches('[data-clive-sub-mode]') ||
      t.matches('[data-clive-sub-select]') ||
      t.matches('[data-variant-select]')
    ) {
      var form = formFromEvent(t);
      if (form) update(form);
      else updateAll();
    }
  });

  // The stepper buttons write the quantity input programmatically, which fires no
  // change event in every theme path, so cover input too.
  document.addEventListener('input', function (evt) {
    var t = evt.target;
    if (!t || !t.matches) return;
    if (t.matches('input[name="quantity"]') || t.matches('[data-quantity-input]')) {
      var form = formFromEvent(t);
      if (form) update(form);
    }
  });

  document.addEventListener('product:variant-change', function (evt) {
    var variant = evt.detail && evt.detail.variant;
    var all = forms();
    for (var i = 0; i < all.length; i++) {
      var form = all[i];
      // theme.js dispatches on document with no section scope. Stamp the new price on
      // the form that owns this variant so products without a subscription block still
      // have a current figure to read.
      if (variant && form.querySelector('option[value="' + variant.id + '"]')) {
        form.setAttribute('data-clive-variant-cents', variant.price);
      }
      update(form);
    }
  });

  function init() {
    updateAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('quickview:loaded', init);
})();
